import type { Recurrence, Weekday } from "@/types/Recurrence";
import { formatGenesisDate } from "@/lib/recurrence";

const WEEKDAY_MAP: Record<Weekday, string> = {
  MO: "Monday",
  TU: "Tuesday",
  WE: "Wednesday",
  TH: "Thursday",
  FR: "Friday",
  SA: "Saturday",
  SU: "Sunday",
};

function getFrequencyLabel(frequency: string, interval?: number) {
  if (frequency === "weekly") {
    if (interval === 2) return "Fortnightly";
    if (interval && interval > 1) return `Every ${interval} weeks`;
    return "Weekly";
  }
  if (frequency === "daily") {
    if (interval && interval > 1) return `Every ${interval} days`;
    return "Daily";
  }
  if (frequency === "monthly") {
    if (interval && interval > 1) return `Every ${interval} months`;
    return "Monthly";
  }
  return frequency.charAt(0).toUpperCase() + frequency.slice(1);
}

function getByWeekdayLabel(byWeekday?: Weekday[]) {
  if (!byWeekday || byWeekday.length === 0) return "";
  if (byWeekday.length === 1) {
    return `every ${WEEKDAY_MAP[byWeekday[0]]}`;
  }
  // Oxford comma for multiple days
  const days = byWeekday.map((d) => WEEKDAY_MAP[d]);
  if (days.length === 2) return `every ${days[0]} and ${days[1]}`;
  return `every ${days.slice(0, -1).join(", ")}, and ${days[days.length - 1]}`;
}

const RecurrenceRenderer = ({ recurrence, recurrencePattern, genesisDate }: { recurrence?: Recurrence | null, recurrencePattern?: Recurrence | null, genesisDate?: Date | string | null }) => {
  const pattern = recurrence || recurrencePattern;
  if (!pattern) return null;
  const { frequency, interval, byWeekday } = pattern;
  const freqLabel = getFrequencyLabel(frequency, interval);
  const weekdayLabel = getByWeekdayLabel(byWeekday);
  const label = [freqLabel, weekdayLabel].filter(Boolean).join(", ");
  const sinceLabel = genesisDate ? `, since ${formatGenesisDate(genesisDate)}` : "";
  return <div>{label}{sinceLabel}</div>;
};

export default RecurrenceRenderer;
